import React, {useMemo} from 'react'
import {FormControl, InputLabel, ListSubheader, MenuItem, Select} from "@mui/material"
import {AnyModelSpecJson} from "../../api/json/ModelSpecJson"
import {ModelPlatform, resolveNameForModelPlatform} from "../../api/enum/ModelPlatform"
import {useIntegrations} from "../../hooks/useIntegrations"
import {useModelSpecs} from "../../hooks/useModelSpecs"

interface GroupedModelSelectProps {
    value?: AnyModelSpecJson
    instructModelsOnly?: boolean
    trainableOnly?: boolean
    onChange: (model?: AnyModelSpecJson) => void
    label?: string
}

function GroupedModelSelect({value, instructModelsOnly, trainableOnly, onChange, label}: GroupedModelSelectProps) {
    const {modelPlatforms} = useIntegrations()
    const {models} = useModelSpecs()

    const grouped = useMemo(() => {
        if (!models || !modelPlatforms) return []
        const allowed = new Set(modelPlatforms)
        const filtered = models
            .filter(m => allowed.has(m.platform))
            .filter(m => !m.deprecated)
            .filter(m => !instructModelsOnly || m.instructTuned)
            .filter(m => !trainableOnly || m.trainable)

        const byPlatform = new Map<ModelPlatform, AnyModelSpecJson[]>()
        filtered.forEach(m => {
            if (!byPlatform.has(m.platform)) byPlatform.set(m.platform, [])
            byPlatform.get(m.platform)!.push(m)
        })

        // Stable platform order matching the integrations list so the groups don't jump around.
        return modelPlatforms
            .filter(p => byPlatform.has(p))
            .map(p => ({platform: p, models: byPlatform.get(p)!}))
    }, [models, modelPlatforms, instructModelsOnly, trainableOnly])

    // MenuItem value is a composite platform:name key to avoid collisions across providers.
    const toKey = (m: AnyModelSpecJson) => `${m.platform}:${m.name}`
    const selectedKey = value ? toKey(value) : ""

    const handleChange = (key: string) => {
        if (!models) return
        const [platform, ...nameParts] = key.split(":")
        const name = nameParts.join(":")
        const found = models.find(m => m.platform === platform && m.name === name)
        onChange(found)
    }

    if (!models || !modelPlatforms) return <></>

    return (
        <FormControl fullWidth>
            <InputLabel id="grouped-model-select-label">{label ?? "Model"}</InputLabel>
            <Select
                labelId="grouped-model-select-label"
                value={selectedKey}
                label={label ?? "Model"}
                onChange={(e) => handleChange(e.target.value)}
            >
                {grouped.flatMap(group => [
                    <ListSubheader key={`header-${group.platform}`}>
                        {resolveNameForModelPlatform(group.platform)}
                    </ListSubheader>,
                    ...group.models.map(m => (
                        <MenuItem key={toKey(m)} value={toKey(m)} sx={{pl: 4}}>
                            {m.displayName}
                        </MenuItem>
                    )),
                ])}
            </Select>
        </FormControl>
    )
}

export default GroupedModelSelect
